import { Type } from 'class-transformer';

import { IPagination, IQueryPaginationDto } from './utils';

// ── Kayıp Yönetim Sistemi durum modeli — TEK KAYNAK (plan §2.2) ──────────────
// Backend'deki src/lostPackages/entities/loss-case.enums.ts ile birebir aynı
// tutulur. İki AYRIK eksen: CaseStatus (makine) + SonucType (inceleme alt-aşaması).

export enum CaseStatus {
  ONAY_BEKLIYOR = 'ONAY_BEKLIYOR',
  INCELEMEYE_ALINDI = 'INCELEMEYE_ALINDI',
  KAYIP_ARASTIRILIYOR = 'KAYIP_ARASTIRILIYOR',
  KAYIP_BULUNDU = 'KAYIP_BULUNDU',
  TAZMIN_EDILDI = 'TAZMIN_EDILDI',
  SONUCLANDI_OLUMLU = 'SONUCLANDI_OLUMLU',
  SONUCLANDI_OLUMSUZ = 'SONUCLANDI_OLUMSUZ',
  REDDEDILDI = 'REDDEDILDI',
  IPTAL = 'IPTAL'
}

export enum LossSource {
  FIRMA_TALEBI = 'FIRMA_TALEBI',
  GONDERI_KAYIP = 'GONDERI_KAYIP',
  KAYIP_BARKODU = 'KAYIP_BARKODU'
}

export const LOSS_SOURCE_LABELS: Record<LossSource, string> = {
  [LossSource.FIRMA_TALEBI]: 'Firma Talebi',
  [LossSource.GONDERI_KAYIP]: 'Gönderiden Kayıp',
  [LossSource.KAYIP_BARKODU]: 'Kayıp Barkodu'
};

// SUPERSET (migration penceresi): eski 4 değer + yeni 2 inceleme alt-aşaması.
// Yeni akışta yalnız TANIMSIZ_URUN_KONTROLU / KAMERA_INCELEMESI kullanılır.
export enum SonucType {
  TAZMIN = 'TAZMIN',
  SONUCLANDI = 'SONUCLANDI',
  BOLGE_TANZIM = 'BOLGE_TANZIM',
  BEKLEMEDE = 'BEKLEMEDE',
  TANIMSIZ_URUN_KONTROLU = 'TANIMSIZ_URUN_KONTROLU',
  KAMERA_INCELEMESI = 'KAMERA_INCELEMESI'
}

// Yalnız geçmiş/etiket amaçlı — yeni akışta rol oynamaz (plan §3.1 not)
export enum StatusType {
  BEKLEMEDE = 'BEKLEMEDE',
  KAYIP_GONDERI = 'KAYIP_GONDERI',
  TRANSFER_GORUNTUSU = 'TRANSFER_GORUNTUSU',
  HASARLI_URUN_KONTROLU = 'HASARLI_URUN_KONTROLU',
  DESI_KONTROLU = 'DESI_KONTROLU',
  TANIMSIZ_URUN_KONTROLU = 'TANIMSIZ_URUN_KONTROLU'
}

export interface DisplayStatusEntry {
  label: string;
  color: string; // antd Tag color
}

// Backend resolveDisplayStatus ile birebir aynı harita (plan §2.2 tablosu)
export const DISPLAY_STATUS: Record<string, DisplayStatusEntry> = {
  [CaseStatus.ONAY_BEKLIYOR]: { label: 'Onay Aşamasında', color: 'default' },
  [CaseStatus.INCELEMEYE_ALINDI]: { label: 'İncelemeye Alındı', color: 'geekblue' },
  'INCELEMEYE_ALINDI:TANIMSIZ_URUN_KONTROLU': {
    label: 'Tanımsız Ürün Kontrolü Devam Ediyor',
    color: 'blue'
  },
  'INCELEMEYE_ALINDI:KAMERA_INCELEMESI': {
    label: 'Kamera İncelemesi Devam Ediyor',
    color: 'orange'
  },
  [CaseStatus.KAYIP_ARASTIRILIYOR]: { label: 'Kayıp (Araştırılıyor)', color: 'purple' },
  [CaseStatus.KAYIP_BULUNDU]: { label: 'Kayıp Bulundu', color: 'cyan' },
  [CaseStatus.SONUCLANDI_OLUMLU]: { label: 'İnceleme Sonuçlandı', color: 'green' },
  [CaseStatus.SONUCLANDI_OLUMSUZ]: {
    label: 'İnceleme Olumsuz Sonuçlandı',
    color: 'red'
  },
  [CaseStatus.TAZMIN_EDILDI]: { label: 'Tazmin Edildi', color: 'gold' },
  [CaseStatus.REDDEDILDI]: { label: 'Reddedildi', color: 'volcano' },
  [CaseStatus.IPTAL]: { label: 'İptal', color: 'default' }
};

export function resolveDisplayStatus(caseStatus?: CaseStatus | null, sonucType?: SonucType | string | null): DisplayStatusEntry {
  if (!caseStatus) return { label: 'Onay Aşamasında', color: 'default' };
  if (caseStatus === CaseStatus.INCELEMEYE_ALINDI && sonucType) {
    const specific = DISPLAY_STATUS[`${caseStatus}:${sonucType}`];
    if (specific) return specific;
  }
  return DISPLAY_STATUS[caseStatus] ?? { label: caseStatus, color: 'default' };
}

export class IDescription {
  description: string;
  creatorUserId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export class ILossDocumentRecipient {
  userId?: number;
  email: string;
  name?: string;
}

export class ILossDocumentRequest {
  id: string;
  type: 'KAMERA_KAYDI' | 'IRSALIYE' | 'TESLIM_BELGESI' | 'DIGER';
  note?: string;
  /** Serbest hedef alıcı e-postası (bölge ya da firma yetkilisi) — 16 Eyl 2026 */
  targetEmail?: string;
  /** Eskalasyon zinciri bölgesi (eski kayıtlarda hedef bölge) */
  targetRegionId?: number;
  targetRecipients: ILossDocumentRecipient[];
  requestedByUserId: number;
  requestedAt: string;
  slaHours: number;
  slaDueAt: string;
  status: 'ACIK' | 'YANITLANDI' | 'ESKALE' | 'KAPANDI' | 'IPTAL';
  respondedAt?: string;
  respondedByUserId?: number;
  /** Public (hesapsız) link ile yanıtlayan alıcı e-postası */
  respondedByEmail?: string;
  /** Hesapsız yükleme bağlantısının geçerlilik sonu */
  uploadTokenExpiresAt?: string;
  /** Bu talebe karşılık yüklenen dosya id'leri */
  uploadedFileIds?: number[];
  escalationLevel: number;
}

export class ILostPackage {
  id: string;
  sonucType?: SonucType;
  statusType?: string;
  creatorId: number;
  creator?: string;
  approverId: number;
  companyId: number;
  company?: string;
  vendorId: number;
  vendor?: string;
  regionId: number;
  region?: string;
  createdAt: string;
  updatedAt: string;
  sonucDatedAt: string;
  @Type(() => IDescription)
  description: IDescription[];
  packagePrice: number;
  barcode: string;
  irsaliyeNumber: string;
  packageType: number;
  packageTypeName?: string;
  notificationDirection: string;
  mail: string;
  // Kayıp Yönetim Sistemi alanları (plan §3.1)
  caseNo?: string;
  source?: LossSource;
  caseStatus?: CaseStatus;
  packageId?: number;
  preLossStatus?: number;
  originalShippingType?: number;
  assigneeUserId?: number;
  assignee?: string;
  acceptedByUserId?: number;
  acceptedBy?: string;
  acceptedAt?: string;
  needsReview?: boolean;
  isLossBarcode?: boolean;
  approvalSlaDueAt?: string;
  resolutionSlaDueAt?: string;
  @Type(() => ILossDocumentRequest)
  documentRequests?: ILossDocumentRequest[];
}

export class ILostPackageResponse {
  @Type(() => ILostPackage)
  result: ILostPackage;
}

export class ILostPackageListResponse extends IPagination {
  @Type(() => ILostPackage)
  results: ILostPackage[];
}

export class ILostPackageQueryDto extends IQueryPaginationDto {
  status?: string;
  toName?: string;
  toId?: string;
  companyId?: string;
  vendorId?: string;
  regionId?: string;
  barcode?: string;
  irsaliyeNumber?: string;
  mail?: string;
  statusType?: StatusType;
  sonucType?: SonucType;
  caseStatus?: CaseStatus;
  source?: LossSource;
  caseNo?: string;
  assigneeUserId?: number;
  state?: 'ongoing' | 'finished';
  create_date_start?: string;
  create_date_end?: string;
}

export class ILostPackageDescriptionPostBodyDto {
  description: string;
}

// PATCH artık yalnız zararsız metadata düzenler (plan §3.5) — statü/caseNo/
// fiyat aksiyon uçlarında yazılır
export class IPatchLostPackageBodyDto {
  mail?: string;
  notificationDirection?: string;
}

// ── Durum makinesi aksiyonları (F2) ──────────────────────────────────────────
// Backend TRANSITION_ENDPOINT_ACTIONS ile aynı; markLost/markFound/compensate/
// abortInvestigation F5'te eklenir
export type LossTransitionAction =
  | 'reject'
  | 'setInvestigation'
  | 'resolvePositive'
  | 'resolveNegative'
  | 'reassign'
  | 'markLost'
  | 'markFound'
  | 'compensate'
  | 'abortInvestigation';

export class IAcceptLossCaseBodyDto {
  caseNo: string; // Kayıp Birimi dosya numarası, serbest format (örn. KYP-2026-08-014)
  assigneeUserId: number;
}

export class ITransitionLossCaseBodyDto {
  action: LossTransitionAction;
  note?: string;
  sonucType?: SonucType;
  assigneeUserId?: number;
}

export class ICancelLossCaseBodyDto {
  note?: string;
}

export class IReopenLossCaseBodyDto {
  note: string;
}

export class IFromShipmentBodyDto {
  barcode: string;
  /** Dosya No — Kayıp Birimi numarası, serbest format (örn. KYP-2026-08-014), zorunlu */
  caseNo: string;
  /** İncelemeci kullanıcı ID (zorunlu) */
  assigneeUserId: number;
  /** İlk açıklama (opsiyonel) */
  note?: string;
}

export type LossDocumentRequestType = 'KAMERA_KAYDI' | 'IRSALIYE' | 'TESLIM_BELGESI' | 'DIGER';

export const LOSS_DOCUMENT_REQUEST_TYPE_LABELS: Record<LossDocumentRequestType, string> = {
  KAMERA_KAYDI: 'Kamera Kaydı',
  IRSALIYE: 'İrsaliye',
  TESLIM_BELGESI: 'Teslim Belgesi',
  DIGER: 'Diğer'
};

export class ICreateDocumentRequestBodyDto {
  type: LossDocumentRequestType;
  /** Hedef alıcı e-postası — bölge ya da firma yetkilisi (zorunlu) */
  targetEmail: string;
  /** Opsiyonel: eskalasyon zinciri bölgesi (verilmezse dosyanın bölgesi) */
  targetRegionId?: number;
  note?: string;
  slaHours?: number;
}

export class IRespondDocumentRequestBodyDto {
  status: 'YANITLANDI' | 'IPTAL';
}

export class ICreateLossBarcodeBodyDto {
  companyId: number;
  vendorId: number;
  packageTypeId: number;
  totalDesi: number;
  note?: string;
  mail?: string;
}

// Auto-fetch yanıtı (plan §4.1) — found=false ise başka HİÇBİR alan gelmez
// (tenant-scope: cross-company veri sızmaz)
export class ILostPackageLookupResponse {
  found: boolean;
  packageId?: number;
  packageType?: number | null;
  companyName?: string | null;
  vendorName?: string | null;
  regionName?: string | null;
  packageTypeName?: string | null;
  irsaliyeNumber?: string | null;
  regionId?: number | null;
  shippingType?: number;
  companyId?: number;
  vendorId?: number;
}

// Create: sunucunun kabul ettiği alanlar (DTO = güvenlik sınırı; sonucType/
// statusType/creatorId/packagePrice artık GÖNDERİLMEZ — backend 400 döner)
// Firma talebi: yalnız barkod + açıklama + mail; firma/bayi/tip/bölge backend'de
// PAKETTEN çözülür (tek giriş barkod)
export class IPostLostPackgeBodyDto {
  barcode: string;
  @Type(() => IDescription)
  description: IDescription[];
  mail: string;
  notificationDirection?: string;
}

export class FileResponseDto {
  id: number;
  packageId: string;
  url: string;
  fileUrl: string;
  date: string; // ISO format (e.g. "2021-09-29T14:00:00.000Z")
  type: string;
  fileId: string;
  status: number;
  userId: number;
}

export class ILostpackagesFilesListResponse {
  @Type(() => FileResponseDto)
  results: FileResponseDto[];
}

// ─── İşlem Geçmişi (lossCaseEvents — dosyaya özel, Türkçe, index'li) ───
export type LossCaseEventActorType = 'USER' | 'SYSTEM';

export class ILossCaseEvent {
  id: string;
  caseId: string;
  /** Makine anahtarı (accept, markLost, documentRequested…) */
  action: string;
  /** Kullanıcıya gösterilen Türkçe başlık */
  title: string;
  /** Türkçe detay (dosya no, incelemeci, not, belge tipi…) */
  message: string;
  actorType: LossCaseEventActorType;
  actorUserId: number | null;
  actorName: string;
  fromStatus: string | null;
  toStatus: string | null;
  fromStatusLabel: string | null;
  toStatusLabel: string | null;
  createdAt: string;
}

export class ILossCaseEventsResponse {
  result: ILossCaseEvent[];
}

// ─── Hesapsız belge yükleme (talep mailindeki token'lı link) ───
export class ILossPublicUploadedFile {
  id: number;
  type: string | null;
  status: number; // 0 bekliyor, 1 yüklendi
  date: string;
}

export class ILossPublicDocumentRequestInfo {
  requestId: string;
  type: LossDocumentRequestType;
  typeLabel: string;
  note: string | null;
  barcode: string | null;
  caseNo: string | null;
  company: string | null;
  requestedAt: string;
  slaDueAt: string;
  expiresAt: string;
  status: 'ACIK' | 'YANITLANDI' | 'ESKALE' | 'KAPANDI' | 'IPTAL';
  canUpload: boolean;
  maxFiles: number;
  maxBytes: number;
  allowedContentTypes: string[];
  uploadedFiles: ILossPublicUploadedFile[];
}

export class ILossPublicUploadRequestBodyDto {
  fileName: string;
  contentType: string;
  size: number;
}

export class ILossPublicUploadStarted {
  uploadId: number;
  uploadUrl: string;
  contentType: string;
}

export class ILossPublicUploadConfirmed {
  status: number;
  requestStatus: string;
}
